<template>
    <div>
        <rkm-row>
            <rkm-col>
                <rkm-data-table
                    :fields="fields"
                    :repository="repositories.paciente"
                    :request-params="requestParams"
                    :tbody-tr-class="rowClass"
                    :disabled-button-search="true"
                    ref="DataTable"
                    sort-by="nome"
                >
                    <template v-slot:cell(nome)="{ item }">
                        {{ item.nome }}

                        <template v-if="item.nomeSocial">
                            <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>({{ item.nomeSocial }})</strong>
                            </template>

                            <template v-else>
                                ({{ item.nomeSocial }})
                            </template>
                        </template>

                        <template v-if="item.pacienteResponsavelFamiliar">
                            <rkm-icon
                                class="ml-1"
                                icon="crown"
                                title="Responsável familiar"
                            />
                        </template>

                        <template v-if="!!item.deficienciaId">
                            <rkm-icon
                                class="ml-1"
                                icon="wheelchair"
                                title="Deficiente"
                            />
                        </template>
                    </template>

                    <template v-slot:cell(dataNascimento)="row">
                        {{ row.value | formatDate }}
                    </template>

                    <template v-slot:cell(cpf)="row">
                        {{ row.value | formatCpf }}
                    </template>

                    <template v-slot:cell(cns)="row">
                        {{ row.value | formatCns }}
                    </template>

                    <template v-slot:cell(carteiraIdentidadeNumero)="row">
                        {{ row.value | formatRg }}
                    </template>

                    <template v-slot:cell(dnv)="row">
                        {{ row.value | formatDnv }}
                    </template>

                    <template v-slot:cell(telefoneNumero)="row">
                        {{ row.value | formatPhoneOrCellPhone }}
                    </template>

                    <template v-slot:cell(actions)="{ item }">

                        <rkm-button
                            v-if="!item.bloqueado && form.isPermissaoBloquear"
                            authorize="update-cadastro-paciente"
                            color="dark"
                            title="Bloquear paciente"
                            @click="bloquearPaciente(item)"
                            small
                        >
                            <rkm-icon icon="ban" />
                        </rkm-button>

                        <rkm-button
                            v-if="item.bloqueado && form.isPermissaoBloquear"
                            authorize="update-cadastro-paciente"
                            color="success"
                            title="Desbloquear paciente"
                            @click="desbloquearPaciente(item)"
                            small
                        >
                            <rkm-icon icon="check-circle" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="print(item.id)"
                            color="info"
                            authorize="show-cadastro-paciente"
                            title="Imprimir"
                            small
                        >
                            <rkm-icon icon="print" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="$router.push({ name: 'cadastro-paciente-show', params: { id: item.id } })"
                            color="info"
                            authorize="show-cadastro-paciente"
                            title="Visualizar"
                            small
                        >
                            <rkm-icon icon="eye" />
                        </rkm-button>

                        <rkm-button
                            :disabled="handleEdit(item)"
                            @click.stop="$router.push({ name: 'cadastro-paciente-edit', params: { id: item.id } })"
                            color="primary"
                            authorize="update-cadastro-paciente"
                            title="Editar"
                            small
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="handleDestroy(item)"
                            color="danger"
                            authorize="destroy-cadastro-paciente"
                            title="Excluir"
                            small
                        >
                            <rkm-icon icon="trash" />
                        </rkm-button>
                    </template>
                </rkm-data-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    Linhas em <span class="danger">vermelho</span> representam pacientes em óbito.
                </p>
            </rkm-col>
        </rkm-row>

        <ModalImpressao ref="ModalImpressao" />
    </div>
</template>

<script>
// Components.
import ModalImpressao from './components/impressao-list/Modal'

// Mixins.
import { repository, error } from '@/mixins'

// Filters.
import formats from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'

export default {
    computed: {
        requestParams() {

            return { with_paciente_bloqueado: true,  with_paciente_provisorio: true }
        },
    },
    components: { ModalImpressao },
    created() {

        this.repositories.paciente = this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente')
    },
    data: () => ({
        fields: [
            {
                name: 'cadastroCidadao.nome',
                key: 'nome',
                label: 'Nome (Nome social)',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeSocial',
                key: 'nomeSocial',
                label: 'Nome social',
                searchable: true,
                sortable: true,
                visible: false
            },
            {
                name: 'cadastroCidadao.dataNascimento',
                key: 'dataNascimento',
                label: 'Data de nascimento',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeMae',
                key: 'nomeMae',
                label: 'Nome da mãe',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroPaciente.prontuario',
                key: 'prontuario',
                label: 'Prontuário municipal',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cpf',
                key: 'cpf',
                label: 'CPF',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cns',
                key: 'cns',
                label: 'CNS',
                searchable: true,
                sortable: true
            },
            {
                name: 'carteiraIdentidadeNumero',
                key: 'carteiraIdentidadeNumero',
                label: 'RG',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.dnv',
                key: 'dnv',
                label: 'DNV',
                searchable: true,
                sortable: true
            },
            {
                name: 'telefoneNumero',
                key: 'telefoneNumero',
                label: 'Telefone',
                searchable: true,
                sortable: true
            },
            {
                name: 'dataHoraUltimaAtualizacao',
                key: 'dataHoraUltimaAtualizacao',
                label: 'Última atualização',
                searchable: true,
                sortable: true
            },
            {
                name: 'actions',
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        enums: { preferenciaNome, classificacao }
    }),
    filters: {
        formatDate: formats.formatDate,
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
        formatRg: formats.formatRg,
        formatPhoneOrCellPhone: formats.formatPhoneOrCellPhone,
        formatDnv: formats.formatDnv,
    },
    methods: {
        handleDestroy({ id }) {

            const destroy = () => this.repositories.paciente.destroy(id)
                .then(() => this.$refs.DataTable.refresh())

            this.showDeleteConfirmation('Deseja realmente excluir o paciente?', destroy, this.handleError)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-danger'
                }
            }
        },
        bloquearPaciente(item) {

            const bloquear = () => this.repositories.paciente.bloquear(item.id)
                .then(() => this.$refs.DataTable.refresh())

            this.createNotification({
                type: 'dialog',
                color: 'danger',
                title: 'Confirmação de bloqueio',
                message: `Deseja bloquear o paciente ${item.nome}?`,
                confirm: bloquear,
                onError: this.handleError,
            })
        },
        desbloquearPaciente(item) {

            const desbloquear = () => this.repositories.paciente.desbloquear(item.id)
                .then(() => this.$refs.DataTable.refresh())

            this.createNotification({
                type: 'dialog',
                color: 'success',
                title: 'Confirmação de desbloqueio',
                message: `Deseja desbloquear o paciente ${item.nome}?`,
                confirm: desbloquear,
                onError: this.handleError,
            })
        },
        handleEdit(item) {

            if (!this.form.isPermissaoCadastro) {
                return true;
            }

            if (this.form.isNotParametrizado) {
                return false;
            }

            const classificacoes = this.form.classificacoes;
            const classificacaoId = item.classificacaoId;

            const isClassificacaoPresent = (classificacaoId) => {
                return classificacoes.some(classificacao => classificacao.id === classificacaoId);
            };

            switch (classificacaoId) {
                case this.enums.classificacao.MUNICIPE:
                case this.enums.classificacao.PROVISORIO:
                case this.enums.classificacao.OUTRO_MUNICIPIO:
                    return !isClassificacaoPresent(classificacaoId);
                default:
                    return false;
            }
        },
        print(id) {
            this.$refs.ModalImpressao.open(id)
        },
    },
    mixins: [ repository, error ],
    props: {
        form: { type: Object, required: true },
    },
}
</script>

<style scoped>
.danger {
    color: var(--danger);
}
</style>